import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { Responsive, WidthProvider } from "react-grid-layout";
import { Flex } from "../../../../components/Container/Flex";
import Button from "../../../../components/Controls/Button";
import { Secondary } from "../../../../utils/style/texts";
import { Colors } from "../../../../utils/style/colors";
import { CloseIcon, EditIcon, PlusIcon } from "../../../../utils/icons";
import * as styles from "../style";
import AppTile from "./AppTile";
import Drawer from "../Drawer";
import { setEditAppPage } from "../actions";
import { selectEditAppPage } from "../selectors";

const ResponsiveGridLayout = WidthProvider(Responsive);

require("../grid-layout.css");

function mapDispatchToProps(dispatch) {
  return {
    setEditAppPage: (page) => dispatch(setEditAppPage(page)),
  };
}

const mapStateToProps = createStructuredSelector({
  editPage: selectEditAppPage(),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ({ page, editPage, setEditAppPage }) => {
    const { t } = useTranslation();
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [editTile, setEditTile] = React.useState(null);

    React.useEffect(() => {
      if (!editPage) {
        setEditTile(null);
        setDrawerOpen(false);
      }
    }, [editPage]);

    const active = editPage || page;
    if (!active) return null;

    let tiles = [...active.get("tiles")];
    const pageData = active.get("data");
    const columns = active.get("columns") || 2;

    return (
      <styles.GridWrap>
        <styles.ButtonBar show={!!editPage} justify="center" align="center">
          <Button
            color={Colors.success}
            startIcon={<PlusIcon />}
            onClick={() => {
              setEditTile(null);
              setDrawerOpen(true);
            }}
          >
            {t("addTile", { defaultValue: "Add tile" })}
          </Button>
        </styles.ButtonBar>

        <Drawer
          isOpen={drawerOpen}
          onClose={() => {
            setEditTile(null);
            setDrawerOpen(false);
          }}
          editTile={pageData[editTile]}
          onSaveTile={(tileDef, config) => {
            const data = Object.assign({}, editPage.get("data"));
            const i = editTile || editPage.uuid();
            if (!editTile) tiles.push({ i, x: 0, y: 0, w: 1, h: 1 });
            data[i] = { type: tileDef.type, config };
            setEditAppPage(editPage.set("tiles", tiles).set("data", data));
            setEditTile(null);
            setDrawerOpen(false);
          }}
        />

        {tiles.length ? (
          <ResponsiveGridLayout
            // Never `measureBeforeMount`: WidthProvider then observes a probe div it
            // immediately replaces, so the width freezes and x/w (but not the fixed
            // rowHeight) break — no horizontal drag or resize.
            className={editPage ? "appPageGrid resizable layout" : "appPageGrid layout"}
            isDraggable={!!editPage}
            isResizable={!!editPage}
            isBounded
            compactType="vertical"
            containerPadding={[12, 12]}
            margin={[12, 12]}
            breakpoints={{ lg: 0 }}
            cols={{ lg: columns }}
            rowHeight={90}
            draggableCancel=".appTileDot,.react-resizable-handle"
            onLayoutChange={(layout) => {
              // Geometry only — this blob is the contract the App reads, and RGL also
              // emits its own bookkeeping (moved, static, minW/maxW…).
              const tiles = layout.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));
              editPage && setEditAppPage(editPage.set("tiles", tiles));
            }}
          >
            {tiles
              .filter((tile) => pageData[tile.i])
              .map((tile) => {
                const d = pageData[tile.i];
                return (
                  <AppTile
                    key={tile.i}
                    data-grid={tile}
                    type={d.type}
                    config={d.config}
                  >
                    {editPage && (
                      <>
                        <styles.EditDot
                          left
                          visible
                          className="appTileDot"
                          color={Colors.gray60}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={() => {
                            setEditTile(tile.i);
                            setDrawerOpen(true);
                          }}
                        >
                          <EditIcon size={0.55} color={Colors.white} />
                        </styles.EditDot>
                        <styles.EditDot
                          visible
                          className="appTileDot"
                          color={Colors.primary()}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={() => {
                            const remaining = tiles.filter(
                              (x) => x.i !== tile.i
                            );
                            const nextData = fromJS(editPage.get("data"))
                              .delete(tile.i)
                              .toJS();
                            setEditAppPage(
                              editPage
                                .set("tiles", remaining)
                                .set("data", nextData)
                            );
                          }}
                        >
                          <CloseIcon size={0.55} color={Colors.white} />
                        </styles.EditDot>
                      </>
                    )}
                  </AppTile>
                );
              })}
          </ResponsiveGridLayout>
        ) : (
          <Flex col align="center" justify="center" style={{ height: 200 }}>
            <Secondary>
              {t("appPageHasNoTiles", { defaultValue: "No tiles yet" })}
            </Secondary>
          </Flex>
        )}
      </styles.GridWrap>
    );
  }
);
